import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Path → tab title mapping. Every titled route gets `TenderWright — <name>`.
// Detail routes (/proposals/:id/build, /people/:id, /projects/:id) match the
// prefix and reuse the parent title — there are too many possible IDs to
// individually map and the parent name is enough context for tab-strip
// recognition.
//
// Order matters: longest prefix first wins, so `/proposals/:id/compliance-matrix`
// hits "Compliance Matrix" before "/proposals" hits "Proposals".
const TITLE_MAP: Array<[RegExp, string]> = [
  [/^\/proposals\/[^/]+\/compliance-matrix$/, 'Compliance Matrix'],
  [/^\/proposals\/[^/]+\/build$/, 'Proposal Builder'],
  [/^\/proposals\/[^/]+$/, 'Proposal'],
  [/^\/people\/[^/]+$/, 'Team Member'],
  [/^\/projects\/[^/]+$/, 'Project'],
  [/^\/p\/[^/]+$/, 'Shared Proposal'],
  [/^\/sme\/[^/]+$/, 'SME Form'],

  [/^\/dashboard$/, 'Dashboard'],
  [/^\/proposals$/, 'Proposals'],
  [/^\/projects$/, 'Projects'],
  [/^\/people$/, 'People'],
  [/^\/templates$/, 'Templates'],
  [/^\/fee-estimator$/, 'Fee Estimator'],
  [/^\/analytics$/, 'Analytics'],
  [/^\/advanced-analytics$/, 'Advanced Analytics'],
  [/^\/achievements$/, 'Achievements'],
  [/^\/rfp-parser$/, 'RFP Parser'],
  [/^\/go-no-go$/, 'Go / No-Go'],
  [/^\/nz-govt-wizard$/, 'NZ Govt Wizard'],
  [/^\/capability-statement$/, 'Capability Statement'],
  [/^\/content-library$/, 'Content Library'],
  [/^\/tenders$/, 'Tender Monitor'],
  [/^\/integrations$/, 'Integrations'],
  [/^\/billing$/, 'Billing'],
  [/^\/intelligence$/, 'Intelligence'],
  [/^\/impact$/, 'Impact Report'],
  [/^\/clients$/, 'Client Intelligence'],
  [/^\/competitors$/, 'Competitor Intelligence'],
  [/^\/certifications$/, 'Certifications'],
  [/^\/audit-log$/, 'Audit Log'],
  [/^\/decisions$/, 'Decision Log'],
  [/^\/ai-usage$/, 'AI Usage'],
  // F129 (Phase 19 audit 2026-05-19): /lessons and /api-keys both
  // fell through to bare "TenderWright" — the title-map covered most
  // sidebar entries but missed these two. Browser tab strip now reads
  // "TenderWright — Lessons" / "TenderWright — API & Webhooks".
  [/^\/lessons$/, 'Lessons'],
  [/^\/api-keys$/, 'API & Webhooks'],
  // F179.2 (audit 2026-05-22): settings sub-routes (/settings/api,
  // /settings/branding, /settings/tender-sources) fell through to bare
  // "TenderWright" because the map only had the /settings root + the
  // legacy /branding /api-keys /tender-settings redirect sources. After
  // redirect the final URL is /settings/<tab>, which had no entry.
  // Order matters: specific sub-routes BEFORE the catch-all /settings/:tab
  // and the /settings root.
  [/^\/settings\/api$/, 'API & Webhooks'],
  [/^\/settings\/branding$/, 'Branding'],
  [/^\/settings\/tender-sources$/, 'Tender Sources'],
  [/^\/settings\/[^/]+$/, 'Settings'],
  [/^\/settings$/, 'Settings'],
  [/^\/resource-planner$/, 'Resource Planner'],
  [/^\/help$/, 'Help'],
  [/^\/branding$/, 'Branding'],
  [/^\/tender-settings$/, 'Tender Sources'],

  [/^\/login$/, 'Sign in'],
  [/^\/register$/, 'Create account'],
  [/^\/forgot-password$/, 'Forgot password'],
  [/^\/reset-password$/, 'Reset password'],
  [/^\/accept-invite$/, 'Accept invite'],
  [/^\/pricing$/, 'Pricing'],
];

const BASE_TITLE = 'TenderWright';

function titleFor(pathname: string): string {
  for (const [pattern, name] of TITLE_MAP) {
    if (pattern.test(pathname)) return `${BASE_TITLE} — ${name}`;
  }
  return BASE_TITLE;
}

/**
 * Renders nothing. Mount once inside the router so every navigation
 * updates document.title. Without this, every browser tab + history
 * entry reads "TenderWright" with no context.
 */
export default function RouteTitle(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleFor(pathname);
  }, [pathname]);
  return null;
}
